<template>
  <div
    v-if="isAnesthesiaIndicationRequired"
    class="book_surgery toggleContainer desk_inner_width"
    :class="{ active: isOpen }"
    ref="anesthesiaIndication"
  >
    <a
      class="book_surgery_top flex"
      href="javascript:void(0)"
      @click.prevent="isOpen = !isOpen"
    >
      <span class="title flex is--fit desk-flex--between">
        <span>אינדיקציה לביצוע הפעולה תחת הרדמה*</span>
        <Tags :isRequired="true" />
      </span>
      <span class="plus-wrap flex m-flex--center">
        <span class="plus"></span>
      </span>
    </a>

    <div v-show="isOpen" class="book_surgery_open toggleInner">
      <div
        class="inner_group bg--white desk-no-pad desk-flex desk-flex--top desk-flex--start"
      >
        <!-- BMI Option -->
        <div
          class="indication-card"
          :class="{ 'indication-card--active': formData.hasBMI }"
        >
          <div class="add_new_row flex anesthesia-checkbox-row">
            <div class="form_check form_check-black">
              <label>
                <input type="checkbox" v-model="formData.hasBMI" />
                BMI (נדרש מעל 40)
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- BMI Fields (shown when BMI is checked) -->
          <div v-if="formData.hasBMI" class="indication-fields">
            <div class="add_new_row flex">
              <div class="add_new_row_col1">גובה (ס״מ)*</div>
              <div class="add_new_row_col2">
                <div class="input-opacity desk-block">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    max="300"
                    v-model.number="formData.height"
                    placeholder="לדוגמה: 175"
                    autocomplete="off"
                  />
                </div>
              </div>
            </div>
            <div class="add_new_row flex">
              <div class="add_new_row_col1">משקל (ק״ג)*</div>
              <div class="add_new_row_col2">
                <div class="input-opacity desk-block">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="500"
                    v-model.number="formData.weight"
                    placeholder="לדוגמה: 70"
                    autocomplete="off"
                  />
                </div>
              </div>
            </div>
            <div v-if="calculatedBMI" class="add_new_row flex">
              <div class="add_new_row_col1">BMI מחושב</div>
              <div class="add_new_row_col2">
                <div
                  class="bmi-result"
                  :class="{ 'bmi-valid': calculatedBMI >= 40 }"
                >
                  {{ calculatedBMI.toFixed(1) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- OSA Option -->
        <div
          class="indication-card"
          :class="{ 'indication-card--active': formData.hasOSA }"
        >
          <div class="add_new_row flex anesthesia-checkbox-row">
            <div class="form_check form_check-black">
              <label>
                <input type="checkbox" v-model="formData.hasOSA" />
                OSA (דרגת חומרה בינונית ומעלה בלבד)
                <span class="checkmark"></span>
              </label>
            </div>
          </div>

          <!-- OSA Severity (shown when OSA is checked) -->
          <div v-if="formData.hasOSA" class="indication-fields">
            <div class="add_new_row flex">
              <div class="add_new_row_col1">דרגת חומרה*</div>
              <div class="add_new_row_col2">
                <div class="input-opacity desk-block">
                  <select2 dir="rtl" v-model="formData.osaSeverity">
                    <option value="">בחר דרגה</option>
                    <option value="mild">קלה</option>
                    <option value="moderate">בינונית</option>
                    <option value="severe">קשה</option>
                  </select2>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Medical Condition Option (disabled for launch; re-enable via flag) -->
        <div
          v-if="ENABLE_MEDICAL_CONDITION_INDICATION"
          class="indication-card"
          :class="{ 'indication-card--active': formData.hasMedicalCondition }"
        >
          <div class="add_new_row flex anesthesia-checkbox-row">
            <div class="form_check form_check-black">
              <label>
                <input type="checkbox" v-model="formData.hasMedicalCondition" />
                מצב בריאותי המחייב הרדמה
                <span class="checkmark"></span>
              </label>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="showErrorMessage" class="add_new_row flex">
          <span class="error-message">{{ errorMessage }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from "vuex";
import Tags from "@/components/Dashboard/Surgery/AddSurgery/Steps/SurgeryDetails/modules/Tags.vue";
import NewFeature from "@/components/Popups/NewFeature.vue";
import {
  ENABLE_MEDICAL_CONDITION_INDICATION,
  ANESTHESIA_DIAGNOSIS_CODE_PREFIXES,
  BMI_40_PLUS_CODE_PATTERN,
  HOSPITAL_ANESTHETIST_VALUE
} from "@/utilities/anesthesia-indication.js";

// Auto-generated indication summary at the top of treatment history, e.g.
// "גובה: 175 | משקל: 70 | מדד BMI: 42.9 | דרגת OSA: בינונית".
// Every value gets its own pipe-separated segment, and each segment starts with
// a Hebrew word so the Latin acronyms stay next to their value in the RTL text.
const INDICATION_SEGMENT = [
  "גובה: [\\d.]+",
  "משקל: [\\d.]+",
  "(?:מדד )?BMI: [\\d.]+",
  "(?:דרגת )?OSA: (?:קלה|בינונית|קשה)"
].join("|");
// Separators of older summaries (plain spaces) are accepted so that editing a
// surgery saved before this format still replaces the whole line.
const INDICATION_SUMMARY_LINE_REGEX = new RegExp(
  `^(?:${INDICATION_SEGMENT})(?:[ \\t]*\\|?[ \\t]*(?:${INDICATION_SEGMENT}))*\\n?`
);
const HEIGHT_CAPTURE_REGEX = /גובה: ([\d.]+)/;
const WEIGHT_CAPTURE_REGEX = /משקל: ([\d.]+)/;

export default {
  name: "AnesthesiaIndication",
  components: {
    Tags
  },
  data() {
    return {
      ENABLE_MEDICAL_CONDITION_INDICATION,
      isOpen: true,
      formData: {
        hasBMI: false,
        height: null,
        weight: null,
        hasOSA: false,
        osaSeverity: "",
        hasMedicalCondition: false
      },
      showErrorMessage: false,
      errorMessage: "יש לבחור לפחות אינדיקציה אחת",
      // Diagnosis codes loaded from API
      anesthesiaDiagnosisCodes: {
        BMI: null,
        OSA: null,
        MEDICAL_CONDITION: null
      }
    };
  },
  computed: {
    ...mapState("Surgery", {
      surgery: state => state.surgery,
      isEditMode: state => state.isEditMode
    }),
    ...mapGetters("Surgery", ["isAnesthesiaIndicationRequired"]),

    calculatedBMI() {
      if (!this.formData.height || !this.formData.weight) return null;
      if (this.formData.height <= 0 || this.formData.weight <= 0) return null;
      const heightInMeters = this.formData.height / 100;
      return this.formData.weight / (heightInMeters * heightInMeters);
    },

    isBMIValid() {
      if (!this.formData.hasBMI) return true;
      return this.calculatedBMI && this.calculatedBMI >= 40;
    },

    isOSAValid() {
      if (!this.formData.hasOSA) return true;
      return (
        this.formData.osaSeverity === "moderate" ||
        this.formData.osaSeverity === "severe"
      );
    },

    hasAnyValidIndication() {
      const hasBMI = this.formData.hasBMI && this.isBMIValid;
      const hasOSA = this.formData.hasOSA && this.isOSAValid;
      const hasMedical =
        ENABLE_MEDICAL_CONDITION_INDICATION &&
        this.formData.hasMedicalCondition;
      return hasBMI || hasOSA || hasMedical;
    },

    hasAnyIndicationSelected() {
      return this.isIndicationSelected(this.formData);
    }
  },
  watch: {
    isAnesthesiaIndicationRequired(newVal) {
      if (!newVal) {
        this.resetForm();
        this.removeAnesthesiaDiagnoses();
        this.SET_SURGERY({ pre_surgery_override: false });
      }
    },
    formData: {
      handler(newVal) {
        this.SET_SURGERY({ anesthesia_indication: { ...newVal } });
        this.updateAnesthesiaDiagnoses(newVal);
        this.updatePreSurgeryOverride(newVal);
        this.updateHospitalAnesthesiaPresence(newVal);
        this.updateTreatmentHistoryIndications(newVal);
      },
      deep: true
    }
  },
  async created() {
    if (this.surgery.anesthesia_indication) {
      this.formData = { ...this.surgery.anesthesia_indication };
      if (!ENABLE_MEDICAL_CONDITION_INDICATION) {
        this.formData.hasMedicalCondition = false;
      }
    }
    await this.loadDiagnosisCodes();
    this.initFromExistingDiagnoses();

    // The section is rendered before the codes finish loading, so a box ticked
    // in the meantime would otherwise never reach the background diagnoses.
    if (this.hasAnyIndicationSelected) {
      this.updateAnesthesiaDiagnoses(this.formData);
    }
  },
  methods: {
    ...mapMutations("Surgery", ["SET_SURGERY"]),

    async loadDiagnosisCodes() {
      try {
        const diagnosisRes = await this.$API.getTypehead(
          "current_new_diagnosis"
        );
        if (!diagnosisRes) return;

        const options = diagnosisRes
          .map(item => item.diagnosis)
          .filter(Boolean)
          .map(diagnosis => ({
            code: diagnosis.substring(0, diagnosis.indexOf(" ")),
            name: diagnosis
          }));

        this.anesthesiaDiagnosisCodes.BMI = this.findBMIDiagnosis(options);
        this.anesthesiaDiagnosisCodes.OSA =
          options.find(
            option => option.code === ANESTHESIA_DIAGNOSIS_CODE_PREFIXES.OSA
          ) || null;

        if (ENABLE_MEDICAL_CONDITION_INDICATION) {
          this.anesthesiaDiagnosisCodes.MEDICAL_CONDITION = this.findDiagnosisByCodePrefix(
            options,
            ANESTHESIA_DIAGNOSIS_CODE_PREFIXES.MEDICAL_CONDITION
          );
        }
      } catch (error) {
        console.error("Failed to load diagnosis codes:", error);
      }
    },

    /**
     * BMI is an ICD-9 category split into sub-codes, and the fund only covers
     * 40 and above, so prefer that family over any other BMI range.
     */
    findBMIDiagnosis(options) {
      const bmiOptions = options.filter(option =>
        option.code
          .toUpperCase()
          .startsWith(ANESTHESIA_DIAGNOSIS_CODE_PREFIXES.BMI)
      );
      if (!bmiOptions.length) return null;

      return (
        bmiOptions.find(option => BMI_40_PLUS_CODE_PATTERN.test(option.code)) ||
        bmiOptions.find(
          option =>
            option.code.toUpperCase() === ANESTHESIA_DIAGNOSIS_CODE_PREFIXES.BMI
        ) ||
        bmiOptions[0]
      );
    },

    findDiagnosisByCodePrefix(options, prefix) {
      return (
        options.find(option =>
          option.code.toUpperCase().startsWith(prefix.toUpperCase())
        ) || null
      );
    },

    resetForm() {
      this.removeIndicationSummaryFromTreatmentHistory();
      this.formData = {
        hasBMI: false,
        height: null,
        weight: null,
        hasOSA: false,
        osaSeverity: "",
        hasMedicalCondition: false
      };
      this.showErrorMessage = false;
    },

    removeIndicationSummaryFromTreatmentHistory() {
      const currentHistory = this.surgery.treatment_history || "";
      if (!currentHistory) return;

      const updatedHistory = this.stripIndicationSummary(currentHistory);
      if (updatedHistory !== currentHistory) {
        this.SET_SURGERY({ treatment_history: updatedHistory });
      }
    },

    /**
     * Removes the auto-generated BMI/OSA summary line from the top of
     * treatment history, leaving any free-text the doctor wrote.
     */
    stripIndicationSummary(history) {
      return String(history || "")
        .replace(INDICATION_SUMMARY_LINE_REGEX, "")
        .trim();
    },

    setError(show) {
      this.showErrorMessage = show;
      if (show) this.isOpen = true;
    },

    validate() {
      if (!this.isAnesthesiaIndicationRequired) {
        return { valid: true };
      }

      const hasMedicalCondition =
        ENABLE_MEDICAL_CONDITION_INDICATION &&
        this.formData.hasMedicalCondition;

      const hasAnySelection =
        this.formData.hasBMI || this.formData.hasOSA || hasMedicalCondition;

      if (!hasAnySelection) {
        this.errorMessage = "יש לבחור לפחות אינדיקציה אחת";
        this.showErrorMessage = true;
        return { valid: false, error: "no_selection" };
      }

      if (
        this.formData.hasBMI &&
        !this.formData.hasOSA &&
        !hasMedicalCondition
      ) {
        if (!this.calculatedBMI || this.calculatedBMI < 40) {
          this.$modal.show(NewFeature, {
            title: "רופא/ה יקר/ה,",
            content:
              "בהתאם להחלטת הקופה, התחייבות לביצוע הבדיקה עם מרדים תנתן לבעלי BMI של 40 ומעלה.",
            buttonText: "המשך"
          });
          return { valid: false, error: "bmi_too_low" };
        }
      }

      if (
        this.formData.hasOSA &&
        !this.formData.hasBMI &&
        !hasMedicalCondition
      ) {
        if (
          this.formData.osaSeverity === "mild" ||
          !this.formData.osaSeverity
        ) {
          this.$modal.show(NewFeature, {
            title: "רופא/ה יקר/ה,",
            content:
              "בהתאם להחלטת הקופה, התחייבות לביצוע הבדיקה עם מרדים תנתן לבעלי OSA בדרגה בינונית ומעלה.",
            buttonText: "המשך"
          });
          return { valid: false, error: "osa_mild" };
        }
      }

      this.showErrorMessage = false;
      return { valid: true };
    },

    /**
     * Updates past_diagnosis based on anesthesia indication selections
     */
    updateAnesthesiaDiagnoses(newVal) {
      const codes = this.anesthesiaDiagnosisCodes;
      const currentDiagnoses = [...(this.surgery.past_diagnosis || [])];
      let updated = [...currentDiagnoses];

      // Handle BMI diagnosis
      if (codes.BMI) {
        const hasBMI = this.hasDiagnosisCode(updated, codes.BMI.code);
        if (newVal.hasBMI && !hasBMI) {
          updated.push(this.createDiagnosis(codes.BMI));
        } else if (!newVal.hasBMI && hasBMI) {
          updated = this.removeDiagnosisByCode(updated, codes.BMI.code);
        }
      }

      // Handle OSA diagnosis
      if (codes.OSA) {
        const hasOSA = this.hasDiagnosisCode(updated, codes.OSA.code);
        const osaSeverityLabel = this.getOsaSeverityLabel(newVal.osaSeverity);
        if (newVal.hasOSA && !hasOSA) {
          updated.push(this.createDiagnosis(codes.OSA, osaSeverityLabel));
        } else if (!newVal.hasOSA && hasOSA) {
          updated = this.removeDiagnosisByCode(updated, codes.OSA.code);
        } else if (newVal.hasOSA && hasOSA) {
          // Update remarks when severity changes
          updated = this.updateDiagnosisRemarks(
            updated,
            codes.OSA.code,
            osaSeverityLabel
          );
        }
      }

      // Handle Medical Condition diagnosis
      if (ENABLE_MEDICAL_CONDITION_INDICATION && codes.MEDICAL_CONDITION) {
        const hasMedicalCondition = this.hasDiagnosisCode(
          updated,
          codes.MEDICAL_CONDITION.code
        );
        if (newVal.hasMedicalCondition && !hasMedicalCondition) {
          updated.push(this.createDiagnosis(codes.MEDICAL_CONDITION));
        } else if (!newVal.hasMedicalCondition && hasMedicalCondition) {
          updated = this.removeDiagnosisByCode(
            updated,
            codes.MEDICAL_CONDITION.code
          );
        }
      }

      // Only update if there were changes
      if (JSON.stringify(updated) !== JSON.stringify(currentDiagnoses)) {
        this.SET_SURGERY({ past_diagnosis: updated });
      }

      // Clear "healthy" status when any anesthesia indication is selected
      if (
        this.isIndicationSelected(newVal) &&
        this.surgery.background_diagnosis === "healthy"
      ) {
        this.SET_SURGERY({ background_diagnosis: "" });
      }
    },

    /**
     * Checks whether at least one enabled indication is ticked
     */
    isIndicationSelected(formData) {
      return Boolean(
        formData.hasBMI ||
          formData.hasOSA ||
          (ENABLE_MEDICAL_CONDITION_INDICATION && formData.hasMedicalCondition)
      );
    },

    /**
     * Updates pre_surgery_override when medical condition is selected
     */
    updatePreSurgeryOverride(newVal) {
      if (!ENABLE_MEDICAL_CONDITION_INDICATION) return;
      if (!this.isAnesthesiaIndicationRequired) return;

      // When medical condition is checked, set pre_surgery_override to true
      if (newVal.hasMedicalCondition && !this.surgery.pre_surgery_override) {
        this.SET_SURGERY({ pre_surgery_override: true });
      }
    },

    /**
     * The procedure is performed under anesthesia, so the hospital anesthetist
     * is preselected on the tools step once an indication is chosen
     */
    updateHospitalAnesthesiaPresence(newVal) {
      if (!this.isAnesthesiaIndicationRequired) return;
      if (!this.isIndicationSelected(newVal)) return;

      if (this.surgery.anesthesia !== HOSPITAL_ANESTHETIST_VALUE) {
        this.SET_SURGERY({ anesthesia: HOSPITAL_ANESTHETIST_VALUE });
      }
    },

    /**
     * Removes all anesthesia-related diagnoses when indication is no longer required
     */
    removeAnesthesiaDiagnoses() {
      const codes = this.anesthesiaDiagnosisCodes;
      const currentDiagnoses = [...(this.surgery.past_diagnosis || [])];
      let updated = [...currentDiagnoses];

      if (codes.BMI) {
        updated = this.removeDiagnosisByCode(updated, codes.BMI.code);
      }
      if (codes.OSA) {
        updated = this.removeDiagnosisByCode(updated, codes.OSA.code);
      }
      if (ENABLE_MEDICAL_CONDITION_INDICATION && codes.MEDICAL_CONDITION) {
        updated = this.removeDiagnosisByCode(
          updated,
          codes.MEDICAL_CONDITION.code
        );
      }

      if (JSON.stringify(updated) !== JSON.stringify(currentDiagnoses)) {
        this.SET_SURGERY({ past_diagnosis: updated });
      }
    },

    /**
     * Creates a diagnosis object from code info
     */
    createDiagnosis(diagnosisInfo, remarks = "") {
      return {
        side: "",
        diagnosis_number: diagnosisInfo.code,
        diagnosis_name: diagnosisInfo.name,
        notice: remarks
      };
    },

    /**
     * Returns Hebrew label for OSA severity
     */
    getOsaSeverityLabel(severity) {
      const labels = {
        mild: "קלה",
        moderate: "בינונית",
        severe: "קשה"
      };
      return labels[severity] || "";
    },

    /**
     * Updates remarks for an existing diagnosis
     */
    updateDiagnosisRemarks(diagnoses, code, remarks) {
      return diagnoses.map(d => {
        if (d.diagnosis_number === code) {
          return { ...d, notice: remarks };
        }
        return d;
      });
    },

    /**
     * Checks if a diagnosis code already exists in the array
     */
    hasDiagnosisCode(diagnoses, code) {
      return diagnoses.some(d => d.diagnosis_number === code);
    },

    /**
     * Removes a diagnosis by its code
     */
    removeDiagnosisByCode(diagnoses, code) {
      return diagnoses.filter(d => d.diagnosis_number !== code);
    },

    /**
     * Writes selected anesthesia indications into treatment history
     * (אינדיקציה לניתוח), joined with " | " when more than one is selected.
     */
    updateTreatmentHistoryIndications(newVal) {
      const parts = [];

      if (newVal.hasBMI && newVal.height && newVal.weight) {
        const bmi = this.calculatedBMI;
        parts.push(`גובה: ${newVal.height}`, `משקל: ${newVal.weight}`);
        if (bmi) {
          parts.push(`מדד BMI: ${bmi.toFixed(1)}`);
        }
      }

      if (newVal.hasOSA && newVal.osaSeverity) {
        const osaLabel = this.getOsaSeverityLabel(newVal.osaSeverity);
        if (osaLabel) {
          parts.push(`דרגת OSA: ${osaLabel}`);
        }
      }

      const summary = parts.join(" | ");
      const currentHistory = this.surgery.treatment_history || "";
      const rest = this.stripIndicationSummary(currentHistory);
      const updatedHistory = summary
        ? rest
          ? `${summary}\n${rest}`
          : summary
        : rest;

      if (updatedHistory !== currentHistory) {
        this.SET_SURGERY({ treatment_history: updatedHistory });
      }
    },

    /**
     * Initializes checkbox selections based on existing diagnoses in edit mode
     */
    initFromExistingDiagnoses() {
      if (!this.isEditMode || !this.isAnesthesiaIndicationRequired) return;

      const diagnoses = this.surgery.past_diagnosis || [];
      if (diagnoses.length === 0) return;

      const hasBMIDiagnosis = diagnoses.some(d =>
        d.diagnosis_number?.startsWith(ANESTHESIA_DIAGNOSIS_CODE_PREFIXES.BMI)
      );
      const hasOSADiagnosis = diagnoses.some(
        d => d.diagnosis_number === ANESTHESIA_DIAGNOSIS_CODE_PREFIXES.OSA
      );
      const hasMedicalConditionDiagnosis =
        ENABLE_MEDICAL_CONDITION_INDICATION &&
        diagnoses.some(d =>
          d.diagnosis_number?.startsWith(
            ANESTHESIA_DIAGNOSIS_CODE_PREFIXES.MEDICAL_CONDITION
          )
        );

      if (hasBMIDiagnosis && !this.formData.hasBMI) {
        this.formData.hasBMI = true;
        this.extractHeightWeightFromTreatmentHistory();
      }
      if (hasOSADiagnosis && !this.formData.hasOSA) {
        this.formData.hasOSA = true;
        this.extractOsaSeverityFromDiagnosis(diagnoses);
      }
      if (hasMedicalConditionDiagnosis && !this.formData.hasMedicalCondition) {
        this.formData.hasMedicalCondition = true;
      }
    },

    /**
     * Extracts OSA severity from diagnosis remarks in edit mode
     */
    extractOsaSeverityFromDiagnosis(diagnoses) {
      const osaDiagnosis = diagnoses.find(
        d => d.diagnosis_number === ANESTHESIA_DIAGNOSIS_CODE_PREFIXES.OSA
      );
      if (!osaDiagnosis || !osaDiagnosis.notice) return;

      const severityMap = {
        קלה: "mild",
        בינונית: "moderate",
        קשה: "severe"
      };
      const severity = severityMap[osaDiagnosis.notice];
      if (severity) {
        this.formData.osaSeverity = severity;
      }
    },

    /**
     * Extracts height and weight from treatment_history in edit mode
     */
    extractHeightWeightFromTreatmentHistory() {
      const currentHistory = this.surgery.treatment_history || "";
      if (!currentHistory) return;

      const height = parseFloat(
        currentHistory.match(HEIGHT_CAPTURE_REGEX)?.[1]
      );
      const weight = parseFloat(
        currentHistory.match(WEIGHT_CAPTURE_REGEX)?.[1]
      );

      if (!isNaN(height) && height > 0) {
        this.formData.height = height;
      }
      if (!isNaN(weight) && weight > 0) {
        this.formData.weight = weight;
      }
    }
  }
};
</script>

<style scoped>
.anesthesia-checkbox-row {
  width: 100%;
}

.indication-card {
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
  transition: all 0.2s ease;
}

.indication-fields {
  margin-top: 16px;
  padding-top: 16px;
  padding-right: 28px;
  border-top: 1px solid #e0e0e0;
}

.bmi-result {
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-weight: bold;
  display: inline-block;
}

.bmi-result.bmi-valid {
  background-color: #e8f5e9;
  color: #2e7d32;
}

@media (max-width: 768px) {
  .indication-card {
    padding: 0 12px;
    margin: 6px 0;
  }

  .indication-fields {
    margin-top: 0;
    padding-top: 0;
    padding-right: 12px;
    border-top: none;
  }
}
</style>
